<template>
  <div class="agent-markdown" @click="onClick" v-html="html" />
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useRouter } from 'vue-router';
import { plmPath, renderAgentMarkdown } from '@lib/agent-markdown';

const { source } = defineProps<{ source: string }>();

const html = computed(() => renderAgentMarkdown(source));

// A link back into the PLM navigates like any other link in the app, without a reload.
const router = useRouter();
const onClick = (event: MouseEvent) => {
  const anchor = (event.target as HTMLElement).closest('a');
  if (!anchor || anchor.target === '_blank') return;
  const path = plmPath(anchor.getAttribute('href') ?? '');
  if (path === null) return;
  event.preventDefault();
  router.push(path);
};
</script>

<style scoped lang="scss">
@import '@styles/variables.scss';

.agent-markdown {
  font-size: 1.4rem;
  line-height: 2.1rem;
  color: $c-ink-body;
  overflow-wrap: anywhere;

  :deep(p),
  :deep(ul),
  :deep(ol),
  :deep(pre),
  :deep(table),
  :deep(blockquote) {
    margin: 0 0 1rem;
  }

  :deep(> :last-child) {
    margin-bottom: 0;
  }

  :deep(h1),
  :deep(h2),
  :deep(h3),
  :deep(h4) {
    margin: 1.4rem 0 0.6rem;
    font-family: 'Montserrat', sans-serif;
    font-size: 1.4rem;
    font-weight: 700;
    color: $c-ink;
  }

  :deep(ul),
  :deep(ol) {
    padding-left: 2rem;
  }

  :deep(code) {
    padding: 0.1rem 0.4rem;
    border-radius: $br-light;
    background: $c-surface-muted;
    font-size: 1.25rem;
  }

  :deep(pre) {
    padding: 1rem 1.2rem;
    border-radius: $br-medium;
    background: $c-canvas-deep;
    overflow-x: auto;

    code {
      padding: 0;
      background: none;
    }
  }

  :deep(table) {
    border-collapse: collapse;
    font-size: 1.3rem;

    th,
    td {
      padding: 0.4rem 0.8rem;
      border: 1px solid $c-hairline-strong;
      text-align: left;
      vertical-align: top;
    }

    th {
      background: $c-surface-muted;
      font-weight: 600;
    }
  }

  :deep(a) {
    color: $c-blue;
  }

  :deep(blockquote) {
    padding-left: 1.2rem;
    border-left: 3px solid $c-hairline-strong;
    color: $c-ink-soft;
  }
}
</style>
